// Client/src/pages/digi/NewMasterSheet/ResidentDirectory/notices/mediaDerivatives.js
//
// Photos and video get a second, smaller file made here in the browser at upload time:
// a web-sized copy the email embeds, and a still frame with the play triangle already drawn
// into it. Doing it here means no ffmpeg on the server, no transcoding queue, and no CPU
// spike when somebody attaches a 60 MB clip.
//
// The maths is separated from the canvas work so it can be tested without a DOM.

/** Photos are resized to this width for the email; the page still shows the original. */
export const INLINE_MAX_WIDTH = 1200;
export const INLINE_QUALITY = 0.8;
/** Where in the video to grab the still. One second in avoids the black first frame. */
export const POSTER_SECOND = 1;

const PHOTO_EXT = /\.(jpe?g|png|webp)$/i;
const VIDEO_EXT = /\.(mp4|m4v|mov)$/i;

export const isPhotoFile = (file) =>
  !!file && (String(file.type || "").startsWith("image/") || PHOTO_EXT.test(String(file.name || "")));

export const isVideoFile = (file) =>
  !!file && (String(file.type || "").startsWith("video/") || VIDEO_EXT.test(String(file.name || "")));

const stripExt = (name) => String(name || "file").replace(/\.[^.]+$/, "");
export const webName = (name) => `${stripExt(name)}-web.jpg`;
export const posterName = (name) => `${stripExt(name)}-poster.jpg`;

/** The size an image becomes so its width is at most `maxWidth`, shape kept, whole pixels. */
export function fitWithin(width, height, maxWidth) {
  const w = Number(width) > 0 ? Number(width) : 1;
  const h = Number(height) > 0 ? Number(height) : 1;
  if (w <= maxWidth) return { width: Math.round(w), height: Math.round(h) };
  const scale = maxWidth / w;
  return { width: Math.round(w * scale), height: Math.max(1, Math.round(h * scale)) };
}

const canvasToFile = (canvas, filename) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) return reject(new Error("The browser could not produce the image"));
        resolve(new File([blob], filename, { type: "image/jpeg" }));
      },
      "image/jpeg",
      INLINE_QUALITY
    );
  });

/**
 * A web-sized JPEG of a photo, for embedding in the email. Resolves to null rather than
 * throwing: a notice that cannot be shrunk should still send, just without the inline copy.
 */
export async function makeWebCopy(file) {
  try {
    const url = URL.createObjectURL(file);
    try {
      const img = await new Promise((resolve, reject) => {
        const el = new Image();
        el.onload = () => resolve(el);
        el.onerror = () => reject(new Error("The image could not be read"));
        el.src = url;
      });
      const { width, height } = fitWithin(img.naturalWidth, img.naturalHeight, INLINE_MAX_WIDTH);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      return await canvasToFile(canvas, webName(file.name));
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.warn("[notices] web copy skipped:", err && err.message);
    return null;
  }
}

/** Draw the play triangle into the frame, so no email client has to position an overlay. */
function drawPlayBadge(ctx, width, height) {
  const r = Math.max(28, Math.round(Math.min(width, height) * 0.11));
  const cx = width / 2;
  const cy = height / 2;
  ctx.fillStyle = "rgba(0, 0, 0, 0.28)";
  ctx.fillRect(0, 0, width, height);
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = "rgba(255, 255, 255, 0.94)";
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(cx - r * 0.3, cy - r * 0.45);
  ctx.lineTo(cx - r * 0.3, cy + r * 0.45);
  ctx.lineTo(cx + r * 0.5, cy);
  ctx.closePath();
  ctx.fillStyle = "#12263f";
  ctx.fill();
}

/**
 * A still from a video with the play badge drawn in. Resolves to null on any failure —
 * an iPhone codec the browser will not decode must not stop the notice from being sent.
 */
export async function makeVideoPoster(file) {
  try {
    const url = URL.createObjectURL(file);
    try {
      const video = document.createElement("video");
      video.preload = "metadata";
      video.muted = true;
      video.playsInline = true;
      video.src = url;
      await new Promise((resolve, reject) => {
        const done = () => resolve();
        video.onloadeddata = done;
        video.onseeked = done;
        video.onerror = () => reject(new Error("The video could not be read in this browser"));
        video.onloadedmetadata = () => {
          video.currentTime = Math.min(POSTER_SECOND, Math.max(0, (video.duration || 1) / 2));
        };
        setTimeout(() => reject(new Error("The video took too long to read")), 15000);
      });
      const { width, height } = fitWithin(video.videoWidth, video.videoHeight, INLINE_MAX_WIDTH);
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(video, 0, 0, width, height);
      drawPlayBadge(ctx, width, height);
      return await canvasToFile(canvas, posterName(file.name));
    } finally {
      URL.revokeObjectURL(url);
    }
  } catch (err) {
    console.warn("[notices] video poster skipped:", err && err.message);
    return null;
  }
}
